import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/useAuth";
import {
  type FeatureFlagActivityState,
  type FeatureFlagDraft,
  type FeatureFlagRecord,
  type FeatureFlagTarget,
  type FeatureRolloutMode,
  type FeatureTargetType,
  type ProfileRecord,
  type TargetInputState,
  starterFlags,
} from "./types";

export function useFeatureFlagsAdmin() {
  const { user } = useAuth();

  const [featureFlags, setFeatureFlags] = useState<FeatureFlagRecord[]>([]);
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [flagSaving, setFlagSaving] = useState(false);
  const [targetBusyKey, setTargetBusyKey] = useState<string | null>(null);
  const [newFlagKey, setNewFlagKey] = useState("");
  const [newFlagName, setNewFlagName] = useState("");
  const [newFlagDescription, setNewFlagDescription] = useState("");
  const [targetInputs, setTargetInputs] = useState<Record<string, TargetInputState>>({});
  const [flagDrafts, setFlagDrafts] = useState<Record<string, FeatureFlagDraft>>({});
  const [profiles, setProfiles] = useState<ProfileRecord[]>([]);
  const [flagActivity, setFlagActivity] = useState<FeatureFlagActivityState>({
    create: false, saveId: null, toggleId: null, rolloutId: null,
  });

  const logAction = async (action: string, metadata: Record<string, unknown>) => {
    if (!user) return;
    const { error } = await supabase
      .from("admin_actions")
      .insert([{ admin_user_id: user.id, action, metadata: metadata as Json }]);
    if (error) toast.error(`Audit log failed: ${error.message}`);
  };

  const ensureStarterFlags = async () => {
    if (!user) return;
    const { error } = await supabase.from("feature_flags").upsert(
      starterFlags.map((flag) => ({
        key: flag.key, name: flag.name, description: flag.description,
        enabled: false, rollout_mode: "off" as const, created_by: user.id,
      })),
      { onConflict: "key", ignoreDuplicates: true },
    );
    if (error) toast.error(error.message);
  };

  const loadFeatureFlags = async () => {
    setFlagsLoading(true);
    const { data, error } = await supabase
      .from("feature_flags")
      .select("id, key, name, description, enabled, rollout_mode, updated_at, created_at, feature_flag_targets(id, target_type, target_id, created_at)")
      .order("created_at", { ascending: true });
    setFlagsLoading(false);
    if (error) return toast.error(error.message);
    const records = (data as FeatureFlagRecord[]) ?? [];
    setFeatureFlags(records);
    setFlagDrafts((prev) => {
      const next = { ...prev };
      for (const record of records) {
        next[record.id] = {
          name: prev[record.id]?.name ?? record.name,
          description: prev[record.id]?.description ?? (record.description ?? ""),
        };
      }
      return next;
    });
    setTargetInputs((prev) => {
      const next = { ...prev };
      for (const record of records) {
        next[record.id] = prev[record.id] ?? { user: "", workspace: "" };
      }
      return next;
    });
  };

  const loadProfiles = async () => {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, owner_user_id, display_name, workspace_label, account_tier, is_primary")
      .order("created_at", { ascending: false });
    if (error) return toast.error(error.message);
    setProfiles((data as ProfileRecord[]) ?? []);
  };

  useEffect(() => {
    void (async () => {
      await ensureStarterFlags();
      await Promise.all([loadFeatureFlags(), loadProfiles()]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const workspaceOptions = useMemo(
    () => profiles.map((p) => ({
      id: p.id,
      label: p.workspace_label || p.display_name || "Untitled workspace",
      description: `${p.account_tier} workspace`,
      keywords: [p.owner_user_id, p.id],
    })),
    [profiles],
  );

  const userOptions = useMemo(() => {
    const seen = new Set<string>();
    return profiles
      .filter((p) => p.is_primary)
      .filter((p) => { if (seen.has(p.owner_user_id)) return false; seen.add(p.owner_user_id); return true; })
      .map((p) => ({
        id: p.owner_user_id,
        label: p.display_name || p.workspace_label || "Unnamed user",
        description: `${p.account_tier} account`,
        keywords: [p.workspace_label ?? "", p.owner_user_id, p.id],
      }));
  }, [profiles]);

  const workspaceLookup = useMemo(
    () => Object.fromEntries(workspaceOptions.map((w) => [w.id, w.label])),
    [workspaceOptions],
  );
  const userLookup = useMemo(
    () => Object.fromEntries(userOptions.map((u) => [u.id, u.label])),
    [userOptions],
  );

  const featureFlagStats = useMemo(() => {
    const enabledCount = featureFlags.filter((f) => f.enabled).length;
    const allowlistCount = featureFlags.filter((f) => f.rollout_mode === "allowlist").length;
    const targetedCount = featureFlags.reduce((c, f) => c + (f.feature_flag_targets?.length ?? 0), 0);
    return { enabledCount, allowlistCount, targetedCount };
  }, [featureFlags]);

  const getTargetLabel = (targetType: FeatureTargetType, targetId: string) =>
    targetType === "user" ? (userLookup[targetId] ?? "Unknown user") : (workspaceLookup[targetId] ?? "Unknown workspace");

  const refreshAll = () => Promise.all([loadFeatureFlags(), loadProfiles()]);

  const updateTargetInput = (flagId: string, targetType: FeatureTargetType, value: string) => {
    setTargetInputs((prev) => ({
      ...prev,
      [flagId]: {
        user: prev[flagId]?.user ?? "", workspace: prev[flagId]?.workspace ?? "",
        [targetType]: value,
      },
    }));
  };

  const updateDraft = (flagId: string, field: keyof FeatureFlagDraft, value: string) => {
    setFlagDrafts((prev) => ({
      ...prev,
      [flagId]: { name: prev[flagId]?.name ?? "", description: prev[flagId]?.description ?? "", [field]: value },
    }));
  };

  const createFlag = async () => {
    const key = newFlagKey.trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "");
    const name = newFlagName.trim();
    const description = newFlagDescription.trim();
    if (!key) return toast.error("Enter a valid feature key");
    if (!name) return toast.error("Enter a feature name");
    setFlagSaving(true);
    setFlagActivity((p) => ({ ...p, create: true }));
    const { data, error } = await supabase
      .from("feature_flags")
      .insert({ key, name, description: description || null, enabled: false, rollout_mode: "off", created_by: user!.id })
      .select("id, key").single();
    setFlagSaving(false);
    setFlagActivity((p) => ({ ...p, create: false }));
    if (error) return toast.error(error.message);
    await logAction("feature_flag_created", { feature_flag_id: data.id, key });
    setNewFlagKey(""); setNewFlagName(""); setNewFlagDescription("");
    await refreshAll();
    toast.success("Feature flag created");
  };

  const updateFlag = async (
    flag: FeatureFlagRecord,
    updates: Partial<Pick<FeatureFlagRecord, "enabled" | "rollout_mode" | "name" | "description">>,
  ) => {
    const payload = {
      ...(updates.enabled !== undefined ? { enabled: updates.enabled } : {}),
      ...(updates.rollout_mode ? { rollout_mode: updates.rollout_mode } : {}),
      ...(updates.name !== undefined ? { name: updates.name.trim() || flag.name } : {}),
      ...(updates.description !== undefined ? { description: updates.description.trim() || null } : {}),
    };
    const activityKey = updates.enabled !== undefined ? "toggleId" : updates.rollout_mode !== undefined ? "rolloutId" : "saveId";
    setFlagActivity((p) => ({ ...p, [activityKey]: flag.id }));
    const { error } = await supabase.from("feature_flags").update(payload).eq("id", flag.id);
    setFlagActivity((p) => ({ ...p, [activityKey]: null }));
    if (error) return toast.error(error.message);
    const action = updates.enabled !== undefined
      ? "feature_flag_toggle_changed"
      : updates.rollout_mode !== undefined ? "feature_flag_rollout_changed" : "feature_flag_metadata_updated";
    await logAction(action, { feature_flag_id: flag.id, key: flag.key, ...payload });
    await refreshAll();
    toast.success("Feature flag updated");
  };

  const addTargetById = async (flag: FeatureFlagRecord, targetType: FeatureTargetType, targetId: string) => {
    const id = targetId.trim();
    if (!id) return toast.error(`Choose a ${targetType}`);
    if (targetType === "user" && !userOptions.some((o) => o.id === id)) return toast.error("Choose a valid user from the picker");
    if (targetType === "workspace" && !workspaceOptions.some((o) => o.id === id)) return toast.error("Choose a valid workspace from the picker");
    const exists = (flag.feature_flag_targets ?? []).some((t) => t.target_type === targetType && t.target_id === id);
    if (exists) return toast.error("That target is already on the allowlist");
    setTargetBusyKey(`${flag.id}:${targetType}:add`);
    const { error } = await supabase.from("feature_flag_targets").insert({
      feature_flag_id: flag.id, target_type: targetType, target_id: id, created_by: user!.id,
    });
    setTargetBusyKey(null);
    if (error) return toast.error(error.message);
    await logAction("feature_flag_target_added", {
      feature_flag_id: flag.id, key: flag.key, target_type: targetType, target_id: id,
      target_label: getTargetLabel(targetType, id),
    });
    updateTargetInput(flag.id, targetType, "");
    await refreshAll();
    toast.success("Target added");
  };

  const addTarget = (flag: FeatureFlagRecord, targetType: FeatureTargetType) =>
    addTargetById(flag, targetType, targetInputs[flag.id]?.[targetType] ?? "");

  const removeTarget = async (flag: FeatureFlagRecord, target: FeatureFlagTarget) => {
    setTargetBusyKey(target.id);
    const { error } = await supabase.from("feature_flag_targets").delete().eq("id", target.id);
    setTargetBusyKey(null);
    if (error) return toast.error(error.message);
    await logAction("feature_flag_target_removed", {
      feature_flag_id: flag.id, key: flag.key, target_type: target.target_type,
      target_id: target.target_id, target_label: getTargetLabel(target.target_type, target.target_id),
    });
    await refreshAll();
    toast.success("Target removed");
  };

  return {
    user,
    featureFlags,
    flagsLoading,
    flagSaving,
    targetBusyKey,
    newFlagKey, setNewFlagKey,
    newFlagName, setNewFlagName,
    newFlagDescription, setNewFlagDescription,
    targetInputs,
    flagDrafts,
    flagActivity,
    workspaceOptions,
    userOptions,
    featureFlagStats,
    getTargetLabel,
    updateTargetInput,
    updateDraft,
    createFlag,
    updateFlag,
    addTarget,
    addTargetById,
    removeTarget,
  };
}

export type FeatureFlagsAdmin = ReturnType<typeof useFeatureFlagsAdmin>;
